import { PropTypes } from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import CreateTemplateModal from "./components/CreateTemplateModal";
import TemplateSelect from "./components/TemplateSelect";
import { getCurrentUserId, isAdminUser } from "./helpers/currentUser";
import useTemplateManager from "./hooks/useTemplateManager";

const NoteTemplateSelect = ({ body = "", applyTemplate = () => false }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = isAdminUser();
  const currentUserId = getCurrentUserId();

  const formState = useMemo(() => ({ body }), [body]);

  const isDifferent = useCallback((currentFormState, template) => {
    const bodyChanged = currentFormState.body !== (template.body || "");
    return bodyChanged;
  }, []);

  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    templateChanged,
    setTemplateChanged,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isCopying,
    isSharing,
    variablesWarning,
    setVariablesWarning,
    variablesWarningRetry,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    shareTemplate,
    unshareTemplate,
    applyTemplate: trackApply,
  } = useTemplateManager({
    apiPath: "/note_templates",
    apiResourceName: "note_template",
    templateType: "note",
    displayNameSingular: "Note Template",
    displayNamePlural: "Note Templates",
    formState,
    isDifferent,
    handleUnsupportedVariables: true,
    onTemplateCreated: () => {
      setShowCreateModal(false);
    },
  });

  const handleCreate = (payload) => {
    createTemplate(() => payload);
  };

  const updateNoteTemplate = () => {
    if (selectedTemplate?.value) {
      updateTemplate(() => ({
        id: selectedTemplate.value,
        body,
      }));
    }
  };

  return (
    <>
      <TemplateSelect
        templates={templates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        templateChanged={templateChanged}
        setTemplateChanged={setTemplateChanged}
        isLoading={isLoading}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        isCopying={isCopying}
        isSharing={isSharing}
        updateTemplate={updateNoteTemplate}
        deleteTemplate={deleteTemplate}
        copyTemplate={copyTemplate}
        shareTemplate={shareTemplate}
        unshareTemplate={unshareTemplate}
        trackApply={trackApply}
        applyTemplate={applyTemplate}
        onCreate={() => setShowCreateModal(true)}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        infoTooltip="Templates save your note body."
      />
      <CreateTemplateModal
        templateType="note"
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        isCreating={isCreating}
        isAdmin={isAdmin}
        formValues={{ body }}
      />
      <UnsupportedVariablesWarning
        show={!!variablesWarning}
        warning={variablesWarning}
        saving={isCreating || isUpdating}
        onReview={() => setVariablesWarning(null)}
        onSaveAnyway={() => {
          setVariablesWarning(null);
          variablesWarningRetry?.();
        }}
      />
    </>
  );
};

NoteTemplateSelect.propTypes = {
  body: PropTypes.string,
  applyTemplate: PropTypes.func,
};

export default NoteTemplateSelect;
